// src/pages/system/UserProfile.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Grid,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  VpnKey as KeyIcon,
  Save as SaveIcon,
  CloudUpload as UploadIcon,
  CheckCircle as ActiveIcon,
  Shield as ShieldIcon,
  Badge as BadgeIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const UserProfile = () => {
  const { user, refreshAuth } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  // Estados de formularios
  const [profileData, setProfileData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: ''
  });

  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });

  // Inicializar datos al cargar el usuario
  useEffect(() => {
    if (user) {
      setProfileData({
        username: user.username || '',
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || ''
      });
    }
  }, [user]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  // Guardar datos generales del perfil
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch('/api/users/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(profileData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al actualizar el perfil');
      }

      // Actualizar localStorage y contexto Auth
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedUser = {
        ...storedUser,
        username: data.username,
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name
      };

      localStorage.setItem('user', JSON.stringify(updatedUser));
      refreshAuth();

      setSuccess('Perfil actualizado correctamente');
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Guardar cambio de contraseña
  const handleSavePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!passwordData.password) {
      setError('La nueva contraseña no puede estar vacía');
      return;
    }

    if (passwordData.password !== passwordData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/users/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...profileData,
          password: passwordData.password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al cambiar la contraseña');
      }

      setPasswordData({ password: '', confirmPassword: '' });
      setSuccess('Contraseña cambiada exitosamente');
    } catch (err) {
      console.error('Error updating password:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Cargar y comprimir foto de perfil (Avatar)
  const handleAvatarClick = () => {
    fileInputRef.current.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de imagen
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setError('Tipo de archivo no permitido. Solo imágenes JPG, PNG, GIF o SVG.');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    // Compresión mediante Canvas (máximo 250x250 píxeles para optimizar ancho de banda)
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = async () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 250;
        const MAX_HEIGHT = 250;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convertir Canvas a Blob y enviarlo
        canvas.toBlob(async (blob) => {
          if (!blob) {
            setError('Error al procesar la imagen.');
            setLoading(false);
            return;
          }

          const formData = new FormData();
          formData.append('avatar', blob, 'avatar.png');

          try {
            const response = await fetch('/api/users/profile/avatar', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: formData
            });

            const data = await response.json();

            if (!response.ok) {
              throw new Error(data.message || 'Error al subir la imagen');
            }

            // Actualizar localStorage y contexto Auth
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            const updatedUser = {
              ...storedUser,
              avatar: data.avatarUrl
            };

            localStorage.setItem('user', JSON.stringify(updatedUser));
            refreshAuth();

            setSuccess('Foto de perfil actualizada correctamente');
          } catch (err) {
            console.error('Error uploading avatar:', err);
            setError(err.message);
          } finally {
            setLoading(false);
          }
        }, 'image/png');
      };
    };
  };

  return (
    <Box className="p-4 sm:p-6 max-w-6xl mx-auto">
      {/* Encabezado */}
      <Box className="mb-6">
        <Typography variant="h4" fontWeight="bold" className="text-gray-800">
          Mi Perfil
        </Typography>
        <Typography variant="body2" className="text-gray-500">
          Administra tus datos personales, contraseña y visualiza tus permisos asignados.
        </Typography>
      </Box>

      {/* Alertas */}
      {error && (
        <Alert severity="error" className="mb-4 rounded-xl border border-red-100 shadow-sm" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* LADO IZQUIERDO: Tarjeta de perfil principal */}
        <Grid item xs={12} md={4}>
          <Card className="rounded-2xl border border-gray-200/60 shadow-xl overflow-hidden bg-white/70 backdrop-blur-md">
            <CardContent className="flex flex-col items-center text-center p-6">
              {/* Imagen interactiva de perfil */}
              <Box className="relative group cursor-pointer mb-4" onClick={handleAvatarClick}>
                <Avatar
                  src={user?.avatar}
                  sx={{
                    width: 120,
                    height: 120,
                    fontSize: '3rem',
                    bgcolor: 'primary.main',
                    boxShadow: '0 8px 24px rgba(33, 150, 243, 0.25)',
                    transition: 'all 0.3s'
                  }}
                  className="group-hover:opacity-85 group-hover:scale-102"
                >
                  {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
                </Avatar>
                
                {/* Overlay de hover de carga */}
                <Box className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <UploadIcon className="text-white" fontSize="large" />
                </Box>
              </Box>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleAvatarChange}
                accept="image/*"
                style={{ display: 'none' }}
              />

              <Typography variant="h5" fontWeight="bold" className="text-gray-800">
                {user?.first_name || user?.username} {user?.last_name || ''}
              </Typography>
              <Typography variant="body2" className="text-gray-500 mb-4 font-medium flex items-center gap-1">
                <EmailIcon fontSize="inherit" /> {user?.email || 'Sin correo electrónico'}
              </Typography>

              <Divider className="w-full my-3" />

              {/* Atributos del rol */}
              <Box className="w-full space-y-2.5 mt-2">
                <Box className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-medium">Nombre de Usuario:</span>
                  <span className="text-gray-700 font-semibold">{user?.username}</span>
                </Box>
                <Box className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-medium">Rol del Sistema:</span>
                  <Chip
                    icon={<ShieldIcon fontSize="inherit" />}
                    label={user?.role === 'admin' ? 'Administrador' : user?.role || 'Operador'}
                    color={user?.role === 'admin' ? 'primary' : 'default'}
                    size="small"
                    variant="outlined"
                    className="font-bold text-xs"
                  />
                </Box>
                <Box className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-medium">Estado de Cuenta:</span>
                  <Chip
                    icon={<ActiveIcon fontSize="inherit" />}
                    label="Activo"
                    color="success"
                    size="small"
                    className="font-bold text-xs"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* LADO DERECHO: Pestañas de configuración */}
        <Grid item xs={12} md={8}>
          <Paper className="rounded-2xl border border-gray-200/60 shadow-xl overflow-hidden bg-white/70 backdrop-blur-md min-h-[420px]">
            <Box className="border-b border-gray-200 bg-gray-50/50">
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="perfil pestañas"
              >
                <Tab icon={<PersonIcon />} iconPosition="start" label="Información General" />
                <Tab icon={<KeyIcon />} iconPosition="start" label="Seguridad y Acceso" />
                <Tab icon={<ShieldIcon />} iconPosition="start" label="Roles y Permisos" />
              </Tabs>
            </Box>

            {/* PESTAÑA 1: Información General */}
            <TabPanel value={tabValue} index={0}>
              <form onSubmit={handleSaveProfile}>
                <Typography variant="h6" fontWeight="bold" className="text-gray-800 mb-4">
                  Datos de Cuenta
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nombre de Usuario"
                      name="username"
                      value={profileData.username}
                      onChange={handleProfileChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Correo Electrónico"
                      name="email"
                      type="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nombres"
                      name="first_name"
                      value={profileData.first_name}
                      onChange={handleProfileChange}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Apellidos"
                      name="last_name"
                      value={profileData.last_name}
                      onChange={handleProfileChange}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
                <Box className="mt-6 flex justify-end">
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    disabled={loading}
                    sx={{ borderRadius: '10px', px: 3, py: 1 }}
                  >
                    {loading ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                </Box>
              </form>
            </TabPanel>

            {/* PESTAÑA 2: Seguridad y Contraseña */}
            <TabPanel value={tabValue} index={1}>
              <form onSubmit={handleSavePassword}>
                <Typography variant="h6" fontWeight="bold" className="text-gray-800 mb-2">
                  Actualizar Contraseña
                </Typography>
                <Typography variant="body2" className="text-gray-500 mb-4">
                  Por seguridad, te aconsejamos usar una combinación robusta de letras, números y caracteres especiales.
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nueva Contraseña"
                      name="password"
                      type="password"
                      value={passwordData.password}
                      onChange={handlePasswordChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Confirmar Contraseña"
                      name="confirmPassword"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
                <Box className="mt-6 flex justify-end">
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<KeyIcon />}
                    disabled={loading}
                    sx={{ borderRadius: '10px', px: 3, py: 1 }}
                  >
                    {loading ? 'Actualizando...' : 'Cambiar Contraseña'}
                  </Button>
                </Box>
              </form>
            </TabPanel>

            {/* PESTAÑA 3: Roles y Permisos (Lectura) */}
            <TabPanel value={tabValue} index={2}>
              <Typography variant="h6" fontWeight="bold" className="text-gray-800 mb-1">
                Panel de Seguridad del Operador
              </Typography>
              <Typography variant="body2" className="text-gray-500 mb-4">
                Listado transparente de todos los accesos específicos concedidos a tu rol en la plataforma.
              </Typography>

              <Divider className="my-3" />

              <Box className="mb-4 flex items-center gap-2">
                <BadgeIcon color="primary" />
                <Typography variant="subtitle2" fontWeight="bold" className="text-gray-700">
                  Rol Asignado: <span className="text-blue-600 capitalize font-extrabold">{user?.role === 'admin' ? 'Administrador del Sistema' : user?.role || 'Operador'}</span>
                </Typography>
              </Box>

              <Typography variant="subtitle2" fontWeight="bold" className="text-gray-700 mb-2">
                Permisos Activos:
              </Typography>
              
              {user?.permissions && user.permissions.length > 0 ? (
                <Box className="flex flex-wrap gap-2.5 max-h-[220px] overflow-y-auto p-1.5 border border-gray-100 rounded-xl bg-gray-50/50">
                  {user.permissions.map((perm, idx) => (
                    <Chip
                      key={idx}
                      label={perm}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: '0.725rem', py: 0.5, borderRadius: '8px' }}
                    />
                  ))}
                </Box>
              ) : (
                <Box className="p-4 bg-blue-50 border border-blue-100 text-blue-700 rounded-xl flex items-center gap-2">
                  <ShieldIcon />
                  <Typography variant="body2">
                    {user?.role === 'admin' 
                      ? 'Como Administrador del Sistema, posees privilegios universales absolutos para todas las funciones.'
                      : 'No posees permisos individuales específicos asignados en tu sesión.'
                    }
                  </Typography>
                </Box>
              )}
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>

      {/* Notificación de Éxito */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" className="rounded-xl shadow-lg">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserProfile;
